// src/hooks/useConstraints.js
import { useState, useCallback } from 'react';
import { ConstraintAPI } from '../services/api/constraintAPI';

/**
 * Hook for managing schedule constraints.
 * Provides CRUD operations and maintains local state.
 */
export function useConstraints() {
  const [constraints, setConstraints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearError = useCallback(() => setError(null), []);

  /**
   * Load constraints from the backend.
   * @param {Object} options - { startDate?, endDate?, crewMemberId? }
   */
  const loadConstraints = useCallback(async (options = {}) => {
    try {
      setLoading(true);
      setError(null);
      const data = await ConstraintAPI.getAll(options);
      setConstraints(data);
      return data;
    } catch (err) {
      console.error('useConstraints: loadConstraints error', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Create a new constraint and add it to local state.
   * @param {Object} constraintData
   * @returns {Promise<Object>} Created constraint
   */
  const createConstraint = useCallback(async (constraintData) => {
    try {
      setLoading(true);
      setError(null);
      const created = await ConstraintAPI.create(constraintData);
      setConstraints(prev => [...prev, created]);
      return created;
    } catch (err) {
      console.error('useConstraints: createConstraint error', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Update an existing constraint.
   * @param {string} constraintId
   * @param {Object} updates
   * @returns {Promise<Object>} Updated constraint
   */
  const updateConstraint = useCallback(async (constraintId, updates) => {
    try {
      setLoading(true);
      setError(null);
      const updated = await ConstraintAPI.update(constraintId, updates);
      setConstraints(prev => prev.map(c => c.id === constraintId ? updated : c));
      return updated;
    } catch (err) {
      console.error('useConstraints: updateConstraint error', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Delete a constraint.
   * @param {string} constraintId
   */
  const deleteConstraint = useCallback(async (constraintId) => {
    try {
      setLoading(true);
      setError(null);
      await ConstraintAPI.delete(constraintId);
      setConstraints(prev => prev.filter(c => c.id !== constraintId));
    } catch (err) {
      console.error('useConstraints: deleteConstraint error', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Exclude a specific date from a weekly constraint (suppress one occurrence).
   * @param {string} constraintId
   * @param {string} date - 'YYYY-MM-DD'
   * @returns {Promise<Object>} Updated constraint
   */
  const excludeDate = useCallback(async (constraintId, date) => {
    try {
      setLoading(true);
      setError(null);
      const updated = await ConstraintAPI.excludeDate(constraintId, date);
      setConstraints(prev => prev.map(c => c.id === constraintId ? updated : c));
      return updated;
    } catch (err) {
      console.error('useConstraints: excludeDate error', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    constraints,
    loading,
    error,
    clearError,
    loadConstraints,
    createConstraint,
    updateConstraint,
    deleteConstraint,
    excludeDate
  };
}
